import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { Input } from '@redpanda/ui';
import FormFieldWithError from '../../components/forms/FormFieldWithError';
import { toast } from 'react-hot-toast';
import { customerAuthApi } from '../../services/api';

interface ForgotPasswordFormData {
  email: string;
}

const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation(['pages', 'translation']);
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>();

  const onSubmit = async (data: ForgotPasswordFormData) => {
    try {
      setSubmitting(true);
      // 傳遞當前語言設定
      const currentLanguage = i18n.language || 'zh';
      const response = await customerAuthApi.forgotPassword(data.email, storeId, currentLanguage);

      // 開發環境下顯示重設連結
      if (response.data.resetUrl) {
        console.log('密碼重設連結:', response.data.resetUrl);
      }

      setSubmitted(true);
      toast.success(t('pages:booking.forgotPassword.sendSuccess'));
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('pages:booking.forgotPassword.sendFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 py-8 px-4">
        <div className="container mx-auto max-w-md">
          {/* 成功提示 */}
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md p-8 text-center">
            <div className="text-6xl mb-4">✅</div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              {t('pages:booking.forgotPassword.successTitle')}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t('pages:booking.forgotPassword.successMessage')}
              <br />
              {t('pages:booking.forgotPassword.checkEmail')}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500 mb-8">
              {t('pages:booking.forgotPassword.linkExpiry')}
            </p>
            <button
              onClick={() => navigate(`/book/${storeId}/login`)}
              className="w-full px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white rounded-lg font-bold transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              {t('pages:booking.forgotPassword.backToLoginButton')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 py-8 px-4">
      <div className="container mx-auto max-w-md">
        {/* 返回按鈕 */}
        <button
          onClick={() => navigate(`/book/${storeId}/login`)}
          className="mb-6 flex items-center space-x-2 text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span>{t('pages:booking.forgotPassword.backToLogin')}</span>
        </button>

        {/* 忘記密碼卡片 */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md p-8">
          <div className="text-center mb-8">
            <div className="text-5xl mb-4">🔑</div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
              {t('pages:booking.forgotPassword.title')}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {t('pages:booking.forgotPassword.subtitle')}
            </p>
          </div>

          {/* 表單 */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Email */}
            <FormFieldWithError
              label={t('pages:booking.forgotPassword.email')}
              required
              error={errors.email?.message}
              htmlFor="email"
            >
              <Input
                id="email"
                type="email"
                placeholder={t('pages:booking.forgotPassword.emailPlaceholder')}
                {...register('email', {
                  required: t('translation:validation.emailRequired'),
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t('translation:validation.emailInvalid'),
                  },
                })}
                variant={errors.email ? 'error' : 'default'}
                disabled={submitting}
              />
            </FormFieldWithError>

            {/* 提交按鈕 */}
            <button
              type="submit"
              disabled={submitting}
              className={`
                w-full px-6 py-3 rounded-lg font-bold text-white transition-all duration-200
                ${
                  submitting
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-brand-600 hover:bg-brand-700 shadow-lg hover:shadow-xl'
                }
              `}
            >
              {submitting ? (
                <span className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>{t('pages:booking.forgotPassword.sending')}</span>
                </span>
              ) : (
                t('pages:booking.forgotPassword.sendButton')
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
